import { formatToISODate } from '@scansource-brazil/zeus-core-ui/date';
import { removeMasks } from '@scansource-brazil/zeus-core-ui/string';
import { AxiosError } from 'axios';
import type { MySsException } from '~/types/MySsException';
import { get } from '~http';

export async function getOrders(resellerDocument: string, params?: any) {
  if (!resellerDocument) throw new Error('RESELLER_DOCUMENT_REQUIRED');
  return await get('orders', { resellerDocument, ...params });
}

export async function listOrderItems(orderNumber: string | number) {
  return await get(`orders/${orderNumber}/items`).then(
    (response: MySsApiResponse) => adaptOrderItems.fromAPI(response.data.data),
    (error: MySsException) => {
      throw error;
    },
  );
}

// apiOrders_v2:

export async function listOrders(resellerDocument: string) {
  return await getOrders(resellerDocument).then(
    (response: any) => ({
      orderList: response.data.data.items.map((orderDto: IOrderDTO) => new Order(orderDto)),
      pagination: response.data.data.pagination,
    }),
    (error: any) => {
      throw error;
    },
  );
}

export async function filterOrders(
  resellerDocument: string,
  filters: IOrderFilters,
  pagination = { ...PAGINATION },
) {
  try {
    const params = adaptQueryParams<IOrderFilters, IOrderFilterParams>(
      filters,
      pagination,
      prepareOrderFilterQueryParams,
    );
    const response = await getOrders(resellerDocument, params);

    return {
      orderList: response.data.data.items.map((orderDto: IOrderDTO) => new Order(orderDto)),
      pagination: response.data.data.pagination,
    };
  } catch (error: any) {
    const err = errorTreatment(error);
    throw err;
  }
}

export async function showOrder(resellerDocument: string, orderNumber: string) {
  return await get(`orders/${orderNumber}`, { resellerDocument }).then(
    (response: MySsApiResponse) => {
      const { data } = response.data;
      return new Order(data);
    },
    (error: MySsException) => {
      throw error;
    },
  );
}

export async function showLastOrder(resellerDocument: string): Promise<IOrder | AxiosError> {
  return await get(`orders/${resellerDocument}/latest`).then(
    (response: MySsApiResponse) => {
      const { data } = response.data;
      return new Order(data);
    },
    (error) => new Error(error),
  );
}

export async function downloadOrderStatement(orderNumber: string) {
  return await get(`orders/${orderNumber}/export`, {}, { responseType: 'blob' }).then(
    ({ data }: any) => {
      downloadFileBlob(data, `${orderNumber}.xlsx`);
    },
    (error: any) => {
      console.error(error);
    },
  );
}

export async function downloadAllOrders(
  resellerDocument: string,
  filters: IOrderFilters,
  pagination = { ...PAGINATION },
) {
  try {
    const params = adaptQueryParams<IOrderFilters, IOrderFilterParams>(
      filters,
      pagination,
      prepareOrderFilterQueryParams,
    );
    params.resellerDocument = resellerDocument;
    return await get('orders/export', params, { responseType: 'blob' }).then(
      ({ data }: any) => {
        downloadFileBlob(
          data,
          `ORDERS_${resellerDocument}_${removeMasks(formatToISODate(new Date(Date.now())))}.xlsx`,
        );
      },
      (error: any) => {
        console.error(error);
      },
    );
  } catch (error: any) {
    const err = errorTreatment(error);
    throw err;
  }
}
